import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Search, SlidersHorizontal, Star } from "lucide-react";

const CATEGORIES = [
  { id: "all", name: "All experiences" },
  { id: "adventure", name: "Adventure" },
  { id: "explore", name: "Explore" },
  { id: "medical", name: "Medical" },
  { id: "relaxation", name: "Relaxation" },
  { id: "fun", name: "Fun" },
  { id: "historic", name: "Historic" },
  { id: "cultural", name: "Cultural" },
  { id: "spiritual", name: "Spiritual" },
];

const PRICE_MAX = 2000;
const DUR_MAX = 240;

// ---------- FilterPanel sub-component ----------
function FilterPanel({ locationInput, setLocationInput, suggestions, priceRange, setPriceRange, durRange, setDurRange, minRating, date, featured, update, applyPrice, applyDur, reset }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="text-sm font-medium">Location</label>
        <Input
          value={locationInput}
          onChange={(e) => setLocationInput(e.target.value)}
          onBlur={(e) => update({ location: e.target.value || undefined })}
          placeholder="e.g. Kerala, Jaipur"
          className="mt-1"
          data-testid="filter-location-input"
        />
        {suggestions.length > 0 && locationInput && (
          <div className="mt-1 grid gap-1">
            {suggestions.slice(0, 5).map((s) => (
              <button
                key={s.location}
                type="button"
                onClick={() => { setLocationInput(s.location); update({ location: s.location }); }}
                className="text-left text-xs px-2 py-1 rounded-md hover:bg-muted"
                data-testid="filter-location-suggestion"
              >
                {s.location} <span className="text-muted-foreground">({s.count})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-medium">Price (USD)</label>
        <div className="text-xs text-muted-foreground mt-0.5">${priceRange[0]} — ${priceRange[1] >= PRICE_MAX ? `${PRICE_MAX}+` : priceRange[1]}</div>
        <div className="py-3"><Slider min={0} max={PRICE_MAX} step={25} value={priceRange} onValueChange={setPriceRange} data-testid="filter-price-slider" /></div>
        <Button size="sm" variant="secondary" onClick={applyPrice} className="rounded-lg" data-testid="filter-price-apply">Apply price</Button>
      </div>

      <div>
        <label className="text-sm font-medium">Duration (hours)</label>
        <div className="text-xs text-muted-foreground mt-0.5">{durRange[0]} — {durRange[1] >= DUR_MAX ? `${DUR_MAX}+` : durRange[1]} hrs</div>
        <div className="py-3"><Slider min={0} max={DUR_MAX} step={2} value={durRange} onValueChange={setDurRange} data-testid="filter-duration-slider" /></div>
        <Button size="sm" variant="secondary" onClick={applyDur} className="rounded-lg" data-testid="filter-duration-apply">Apply duration</Button>
      </div>

      <div>
        <label className="text-sm font-medium">Minimum rating</label>
        <div className="mt-2 flex gap-1 flex-wrap">
          {[0, 3, 4, 4.5].map((r) => (
            <button
              key={r}
              onClick={() => update({ min_rating: r || undefined })}
              className={`text-xs px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${minRating === r ? "bg-[hsl(var(--primary))] text-white border-transparent" : "bg-card border-border"}`}
              data-testid={`filter-rating-${r}`}
            >
              <Star className="h-3 w-3" /> {r === 0 ? "Any" : r}+
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Available on</label>
        <Input type="date" value={date} onChange={(e) => update({ date: e.target.value || undefined })} className="mt-1" data-testid="filter-date-input" />
      </div>

      <div>
        <label className="text-sm font-medium">Featured</label>
        <div className="mt-2 flex gap-2">
          <button
            onClick={() => update({ featured: featured ? undefined : "1" })}
            className={`text-xs px-3 py-1.5 rounded-full border ${featured ? "bg-[hsl(var(--saffron))] text-white border-transparent" : "bg-card border-border"}`}
            data-testid="filter-featured-toggle"
          >
            Featured only
          </button>
        </div>
      </div>

      <Button variant="ghost" onClick={reset} className="rounded-xl w-full" data-testid="filter-reset">Reset all</Button>
    </div>
  );
}

// ---------- ListingsGrid sub-component ----------
function ListingsGrid({ data, loading, page, update, reset }) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (<div key={`skeleton-${i}`} className="rounded-2xl bg-muted h-80 animate-pulse" />))}
      </div>
    );
  }
  if (data.items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="category-empty-state">
        <p className="font-medium">No experiences match your filters</p>
        <p className="text-sm text-muted-foreground mt-1">Try widening price/duration or removing filters.</p>
        <Button onClick={reset} variant="secondary" className="mt-3 rounded-xl">Reset filters</Button>
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.items.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
      </div>
      {data.pages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
          <Button variant="ghost" disabled={page <= 1} onClick={() => update({ page: page - 1 })} className="rounded-xl" data-testid="pagination-prev">Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data.pages}</span>
          <Button variant="ghost" disabled={page >= data.pages} onClick={() => update({ page: page + 1 })} className="rounded-xl" data-testid="pagination-next">Next</Button>
        </div>
      )}
    </>
  );
}

// ---------- Main page ----------
export default function CategoryBrowse() {
  const { categoryId } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);

  // Filter state derived from URL
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const minPrice = Number(params.get("min_price") || 0);
  const maxPrice = Number(params.get("max_price") || PRICE_MAX);
  const minDuration = Number(params.get("min_duration") || 0);
  const maxDuration = Number(params.get("max_duration") || DUR_MAX);
  const minRating = Number(params.get("min_rating") || 0);
  const date = params.get("date") || "";
  const location = params.get("location") || "";
  const featured = params.get("featured") === "1";
  const page = Number(params.get("page") || 1);

  // Controlled inputs with render-phase URL sync (avoids useEffect loops)
  const [qInput, setQInput] = useState(q);
  const [prevQ, setPrevQ] = useState(q);
  const [locationInput, setLocationInput] = useState(location);
  const [prevLoc, setPrevLoc] = useState(location);
  const [priceRange, setPriceRange] = useState([minPrice, maxPrice]);
  const [prevPriceKey, setPrevPriceKey] = useState(`${minPrice}|${maxPrice}`);
  const [durRange, setDurRange] = useState([minDuration, maxDuration]);
  const [prevDurKey, setPrevDurKey] = useState(`${minDuration}|${maxDuration}`);
  const [suggestions, setSuggestions] = useState([]);

  // Render-phase adjustment: sync local inputs when URL params change externally
  if (q !== prevQ) { setPrevQ(q); setQInput(q); }
  if (location !== prevLoc) { setPrevLoc(location); setLocationInput(location); }
  if (`${minPrice}|${maxPrice}` !== prevPriceKey) { setPrevPriceKey(`${minPrice}|${maxPrice}`); setPriceRange([minPrice, maxPrice]); }
  if (`${minDuration}|${maxDuration}` !== prevDurKey) { setPrevDurKey(`${minDuration}|${maxDuration}`); setDurRange([minDuration, maxDuration]); }

  const activeCat = categoryId || params.get("category") || "all";
  const loading = !data;

  // Fetch listings whenever filter params change
  useEffect(() => {
    const search = new URLSearchParams();
    if (activeCat && activeCat !== "all") search.set("category", activeCat);
    if (q) search.set("q", q);
    if (sort) search.set("sort", sort);
    if (minPrice > 0) search.set("min_price", minPrice);
    if (maxPrice < PRICE_MAX) search.set("max_price", maxPrice);
    if (minDuration > 0) search.set("min_duration", minDuration);
    if (maxDuration < DUR_MAX) search.set("max_duration", maxDuration);
    if (minRating > 0) search.set("min_rating", minRating);
    if (date) search.set("date", date);
    if (location) search.set("location", location);
    if (featured) search.set("featured", "true");
    search.set("page", page);
    search.set("page_size", 12);
    const load = async () => {
      try {
        const r = await api.get(`/listings?${search.toString()}`);
        const body = r.data;
        setData(body.items ? body : { items: body, total: body.length, page: 1, pages: 1 });
      } catch (e) { /* ignore */ }
    };
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [activeCat, q, sort, minPrice, maxPrice, minDuration, maxDuration, minRating, date, location, featured, page]);

  // Fetch location autocomplete suggestions
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/locations/suggest?q=${encodeURIComponent(locationInput || "")}`);
        setSuggestions(r.data || []);
      } catch (e) { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [locationInput]);

  const title = useMemo(() => CATEGORIES.find((x) => x.id === activeCat)?.name || "All experiences", [activeCat]);

  const update = (patch) => {
    const p = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === false) p.delete(k);
      else p.set(k, v);
    });
    if (!("page" in patch)) p.delete("page");
    setParams(p);
  };

  const onSubmit = (e) => { e.preventDefault(); update({ q: qInput }); };
  const applyPrice = () => update({ min_price: priceRange[0] || undefined, max_price: priceRange[1] < PRICE_MAX ? priceRange[1] : undefined });
  const applyDur = () => update({ min_duration: durRange[0] || undefined, max_duration: durRange[1] < DUR_MAX ? durRange[1] : undefined });
  const reset = () => setParams(new URLSearchParams());

  const filterProps = { locationInput, setLocationInput, suggestions, priceRange, setPriceRange, durRange, setDurRange, minRating, date, featured, update, applyPrice, applyDur, reset };

  return (
    <div data-testid="category-browse-page">
      <section className="border-b border-border iv-pattern-rangoli">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10 md:py-14">
          <p className="text-sm text-muted-foreground">Browse</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight" data-testid="category-browse-title">{title}</h1>
          <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex items-center flex-1 rounded-2xl bg-card border border-border px-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search experiences, places, themes..." className="border-0 focus-visible:ring-0 bg-transparent" data-testid="category-search-input" />
            </div>
            <Button type="submit" className="rounded-2xl h-11" data-testid="category-search-submit">Search</Button>
          </form>
        </div>
      </section>

      <section className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-4 gap-6">
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-32 rounded-2xl bg-card border border-border p-5" data-testid="filters-sidebar">
              <h3 className="font-semibold mb-4">Filters</h3>
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
              <div className="flex overflow-x-auto no-scrollbar gap-2">
                {CATEGORIES.map((c) => (
                  <Link key={c.id} to={c.id === "all" ? "/search" : `/categories/${c.id}`} className={`text-sm px-3 py-2 rounded-full border whitespace-nowrap ${activeCat === c.id ? "bg-[hsl(var(--primary))] text-white border-transparent" : "bg-card border-border hover:bg-muted"}`} data-testid={`category-chip-${c.id}`}>{c.name}</Link>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" className="rounded-xl lg:hidden" data-testid="open-filters-mobile"><SlidersHorizontal className="h-4 w-4 mr-2" /> Filters</Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="bg-background w-[320px] overflow-y-auto">
                    <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                    <div className="mt-4"><FilterPanel {...filterProps} /></div>
                  </SheetContent>
                </Sheet>
                <Select value={sort} onValueChange={(v) => update({ sort: v })}>
                  <SelectTrigger className="w-[170px]" data-testid="category-sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Top rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-sm text-muted-foreground mb-3" data-testid="results-count">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? "experience" : "experiences"}
            </div>

            <ListingsGrid data={data} loading={loading} page={page} update={update} reset={reset} />
          </div>
        </div>
      </section>
    </div>
  );
}
